/**
 * Shared open/closed state for Niva's drawer.
 *
 * The drawer is rendered by ChatbotBubble (mounted once, app-wide, by
 * ChatbotGate in App.tsx) but it is opened from more than one place: the
 * floating launcher inside that component AND the "Ask Niva" button in the top
 * bar (components/Navigation.tsx), which lives in a completely different branch
 * of the tree. A context is what lets the second one exist without threading
 * props through every shell.
 *
 * It also owns the `GET /chatbot/config` kill-switch fetch, which used to live
 * inside ChatbotBubble. Both consumers need the answer — the top-bar button must
 * not offer a bot that is switched off — and hoisting it here keeps that to one
 * request per session instead of one per consumer.
 *
 * What it deliberately does NOT own: the transcript. That stays local to
 * ChatbotBubble so ChatbotGate's unmount-on-sign-out still discards it.
 * `pendingPrompt` is the one exception in that direction — a question handed IN
 * from elsewhere (the buyer dashboard's suggested prompts) that the drawer sends
 * on its own transcript and then clears. It is a baton, not a copy of the
 * conversation: the alternative was a second chat implementation with its own
 * history and its own copy of the tier gating, which would drift.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { isFreeBuyerUser } from "@/lib/access";

interface NivaContextValue {
  /** Whether the drawer is showing. */
  open: boolean;
  /** Widened (desktop reading) mode. */
  expanded: boolean;
  /**
   * Whether Niva should be offered to this viewer at all: the platform switch is
   * on, someone is signed in, and they are not a free/Basic buyer. Gates both the
   * floating launcher and the top-bar button, so the two can never disagree.
   *
   * NOT an entitlement check — an Explorer buyer past their free week is still
   * "available" and gets the upgrade wall inside the panel. Who may actually
   * send a message is canUseNiva() in lib/access.ts, enforced server-side.
   */
  available: boolean;
  openNiva: () => void;
  closeNiva: () => void;
  setExpanded: (value: boolean | ((prev: boolean) => boolean)) => void;
  /** Open the drawer and ask this, on the drawer's own transcript. */
  askNiva: (question: string) => void;
  /** Set by askNiva, consumed and cleared by the drawer. */
  pendingPrompt: string | null;
  clearPendingPrompt: () => void;
}

const NivaContext = createContext<NivaContextValue>({
  open: false,
  expanded: false,
  available: false,
  openNiva: () => {},
  closeNiva: () => {},
  setExpanded: () => {},
  askNiva: () => {},
  pendingPrompt: null,
  clearPendingPrompt: () => {},
});

export function useNiva() {
  return useContext(NivaContext);
}

export function NivaProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [pendingPrompt, setPendingPrompt] = useState<string | null>(null);
  // null = not asked yet, so nothing offers Niva until we know rather than
  // flashing a launcher in and taking it away again.
  const [enabled, setEnabled] = useState<boolean | null>(null);

  const eligible = !!user && !isFreeBuyerUser(user);

  useEffect(() => {
    if (!eligible) {
      setEnabled(null);
      return;
    }
    let stale = false;
    api
      .get<{ enabled: boolean }>("/chatbot/config")
      .then((c) => { if (!stale) setEnabled(c.enabled); })
      .catch(() => { if (!stale) setEnabled(false); });
    return () => { stale = true; };
  }, [eligible]);

  // Close on sign-out or account switch. The provider sits ABOVE ChatbotGate, so
  // unlike the drawer's transcript this state survives that unmount — without
  // this, the next person to sign in on the same tab would land with Niva
  // already open on top of their page.
  useEffect(() => {
    setOpen(false);
    setExpanded(false);
    setPendingPrompt(null);
  }, [user?.id]);

  const openNiva = useCallback(() => setOpen(true), []);
  const closeNiva = useCallback(() => setOpen(false), []);
  const askNiva = useCallback((question: string) => {
    const text = question.trim();
    if (!text) return;
    setPendingPrompt(text);
    setOpen(true);
  }, []);
  const clearPendingPrompt = useCallback(() => setPendingPrompt(null), []);

  const value = useMemo<NivaContextValue>(
    () => ({
      open,
      expanded,
      available: eligible && enabled === true,
      openNiva,
      closeNiva,
      setExpanded,
      askNiva,
      pendingPrompt,
      clearPendingPrompt,
    }),
    [open, expanded, eligible, enabled, openNiva, closeNiva, askNiva, pendingPrompt, clearPendingPrompt],
  );

  return <NivaContext.Provider value={value}>{children}</NivaContext.Provider>;
}
